import { useEffect, useState, useCallback, useRef } from 'react';
import { useAuthStore } from '@/stores/auth-store';
import { Hub } from 'aws-amplify/utils';
import { fetchAuthSession } from 'aws-amplify/auth';
import { AuthContext } from './authContext';
import { amplifyAuthHandler } from '../services/amplifyAuthHandler';
import { sentryUtils } from '@/utils/sentry';
import { handleAuthFailure } from '@/utils/authRedirect';

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [isLoading, setIsLoading] = useState(true);
  const { user, checkAuth } = useAuthStore();
  const hadUserRef = useRef(false);

  useEffect(() => {
    hadUserRef.current = !!user;
  }, [user]);
  
  // Initialize standard Amplify auth handler
  useEffect(() => {
    amplifyAuthHandler.initialize();
  }, []);

  // Memoize the verifyAuth function to prevent infinite loops
  const verifyAuth = useCallback(async () => {
    try {
      await checkAuth();
    } catch (error) {
      console.log('[AuthProvider][verifyAuth] Error:', error);
    } finally {
      setIsLoading(false);
    }
  }, [checkAuth]);

  useEffect(() => {
    // Initial auth check
    verifyAuth();

    // Set up Hub listener for auth state changes
    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      switch (payload.event) {
        case 'signedIn':
        case 'tokenRefresh':
        case 'signedOut':
          verifyAuth();
          break;
        case 'tokenRefresh_failure':
          handleAuthFailure('Token refresh failed');
          break;
      }
    });

    // Cleanup listener
    return () => {
      unsubscribe();
    };
  }, [verifyAuth]);

  // Returning to a long-idle tab: re-check Cognito before the user hits a dead API
  useEffect(() => {
    const onVisibilityChange = async () => {
      if (document.visibilityState !== 'visible') return;
      if (!hadUserRef.current) return;

      try {
        const session = await fetchAuthSession();
        if (!session.tokens?.idToken) {
          handleAuthFailure('Session expired while tab was idle');
          return;
        }
        await verifyAuth();
      } catch (error) {
        console.warn('[AuthProvider] Session check on focus failed:', error);
        handleAuthFailure('Session check failed on tab focus');
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [verifyAuth]);

  // Track user authentication state in Sentry
  useEffect(() => {
    if (user) {
      sentryUtils.setUser({
        id: user.sub,
        email: user.attributes?.email,
        username: user.attributes?.preferred_username || user.attributes?.name,
        role: user.attributes?.['custom:role'] || 'user',
      });
      sentryUtils.addBreadcrumb('User signed in', 'auth', 'info', { userId: user.sub });
    } else {
      sentryUtils.clearUser();
      sentryUtils.addBreadcrumb('User signed out', 'auth', 'info');
    }
  }, [user]);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <AuthContext.Provider value={{ isLoading, isAuthenticated: !!user }}>
      {children}
    </AuthContext.Provider>
  );
} 